import type {
  AllocationMethodologies,
  BulkTradeRunReasons,
  ModelPortfolioTypes,
  PortfolioReportRefreshStates,
} from '@generated/graphql';
import type { CurrencyCodes } from '@onevesthq/ov-enums';
import type { ClientGroupReportObject } from './clientGroupReport';
import type { FinancialProduct } from './financialProduct';
import type {
  Goal as GoalType,
  SubAccount as SubAccountType,
} from './goalSubAccountTypes';
import type { HoldingReport } from './holdingReport';
import type { PortfolioOptimizer } from './portfolioOptimizer';
import type { SubAccountReport } from './subAccountReport';

export type BulkTradeRunReason = {
  reason: BulkTradeRunReasons;
  total: number;
  unblockedSince?: Date;
};

export type FxRateDocument = {
  from: CurrencyCodes;
  to: CurrencyCodes;
  rate: number;
};

export type PortfolioReport = {
  id: string;
  baseCurrency: CurrencyCodes;
  originalCurrencies?: CurrencyCodes[];
  fxRates?: FxRateDocument[];
  modelPortfolio: FinancialProduct;
  modelPortfolioType: ModelPortfolioTypes;
  object: GoalType | SubAccountType | ClientGroupReportObject;
  allocationMethodology: AllocationMethodologies;
  cashCents: number;
  expectedCashCents: number;
  expectedCashPercentage: number;
  cashAssetClass?: { id: string } | null;
  cashAssetClassGroupPercentage?: number | null;
  cashAssetClassPercentage?: number | null;
  holdings: HoldingReport[];
  driftRebalanceRequired: boolean;
  liquidateRebalanceRequired: boolean;
  transferRebalanceRequired: boolean;
  pendingSubTradeRequests: number;
  lastOptimizedBy: PortfolioOptimizer;
  cashAvailableForTradeCents: number;
  bulkTradeRunReasons: BulkTradeRunReason[];
  subAccounts: SubAccountReport[];
  updatedAt?: Date;
  refreshState: PortfolioReportRefreshStates;
  [key: string]: any;
};

export const isGoal = (object: PortfolioReport['object']): object is GoalType =>
  object.__typename === 'Goal';
export const isSubAccount = (
  object: PortfolioReport['object'],
): object is SubAccountType => object.__typename === 'SubAccount';
export const isClientGroup = (
  object: PortfolioReport['object'],
): object is ClientGroupReportObject => object.__typename === 'ClientGroup';
